import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  linkedSignal,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { BehaviorSubject, forkJoin, from, of, switchMap, finalize, take, concatMap } from 'rxjs';
import {
  API,
  BookingDTO,
  CampaignDTO,
  CampaignWithPreviewDTO,
  CommercialFullDTO,
  CompanyDTO,
  ds_cm_Campaign_retrieveAllFrontendEnhanced_order,
  MediumDTO,
  Order,
  Pagination,
  ReportingFullDTO,
  RequirementFullDTO,
  TimingStatus,
} from '../../../../services/api/api';
import { ErrorService } from '../../../../services/error.service';
import { ModalService } from '../../../../services/gui/modal/modal.service';
import { UserService } from '../../../../services/user.service';
import { UtilService } from '../../../../services/util.service';
import { CampaignReportingObsService } from '../../../../services/campaign-reporting-obs.service';
import { CampaignWithPreviewReportingDTOAndTags } from '../../../../models/taggable.model';
import {
  CampaignArrangementModalParams,
  SbCampaignOrderingComponent,
} from 'src/app/components/sb-campaign-ordering/sb-campaign-ordering.component';
import { TranslateService } from '@ngx-translate/core';
import { TagService } from '../../../../services/tag.service';
import { RoutingID } from '../../../../routes/sb-routing-enum';
import { OrderOption, SbBooleanToggleModel, SbSplitButton, SelectionState, Mutable } from '../../../../services/global/global-interfaces';
import { ModalNavigationComponent } from '../../../../services/gui/modal/modal-navigation-component';
import { ModalNavigationService } from '../../../../services/gui/modal/modal-navigation.service';
import { ReportingModalComponent } from 'src/app/components/standard-reporting/reporting-modal/reporting-modal.component';
import { ReportingService } from '../../../../services/reporting.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { SbDateTime, SbLocalDateTime } from '../../../../services/api/api-date-time';
import { PermissionService } from '../../../../services/permission.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilterStorageService } from '../../../../services/filter-storage.service';
import { StringTransformationService } from '../../../../services/transformation-helper/string-transformation.service';
import { DateTransformationService } from '../../../../services/transformation-helper/date-transformation.service';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { MultiSelectButtonAction } from '../../../../components/table-multi-select-button/table-multi-select-button.component';
import { QueryParamService } from 'src/app/services/lists/query-params';
import { BookingService } from '../../../../services/booking.service';
import { RequestClass } from '../../../../services/global/RequestClass';

type SplitButtonAction = 'delete' | 'rearrange' | 'copyCampaign';
type CampaignWithPreviewReportingDTOAndTagsAndButtons = CampaignWithPreviewReportingDTOAndTags & SbSplitButton<SplitButtonAction>;

@Component({
  selector: 'sb-page-campaigns-list',
  templateUrl: './campaigns-list.component.html',
  styleUrls: ['./campaigns-list.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CampaignsListComponent extends RequestClass implements OnInit, ModalNavigationComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);
  protected readonly dateTransformation = inject(DateTransformationService);
  private readonly cdRef = inject(ChangeDetectorRef);

  protected readonly API = API;
  protected readonly JSON = JSON;

  protected readonly ds_cm_Campaign_retrieveAllFrontendEnhanced_order = ds_cm_Campaign_retrieveAllFrontendEnhanced_order;
  rowHeight = signal<number>(100);

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;
  campaigns: CampaignWithPreviewReportingDTOAndTagsAndButtons[];
  selectedCampaigns: Map<number, CampaignWithPreviewReportingDTOAndTagsAndButtons> = new Map();
  selectedCompany: CompanyDTO | null;
  filteredCampaignTypes: number[];
  isInModalContext: boolean;
  modalType: string;

  timingPlanned: boolean;
  timingActive: boolean;
  timingEnded: boolean;
  timingUnknown: boolean;

  pagination: BehaviorSubject<Pagination> | undefined;
  selectedContentCullIds: number[];
  search: string | null = null;
  exactMatch: SearchOptions = 'contains';

  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    autoApply: true,
    button: {
      visibleWithoutChanges: true,
    },
  };

  // refactor into computed of selectedCampaigns when transforming selectedCampaigns to signal and remove recalculateSelectionState
  selectionState: SelectionState = 'none';

  initialBooleans: SbBooleanToggleModel[];

  allOrderOptions: OrderOption<ds_cm_Campaign_retrieveAllFrontendEnhanced_order>[] = [
    {
      value: ds_cm_Campaign_retrieveAllFrontendEnhanced_order.CLIENT_ORDER_NUM,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.clientOrderNum'),
    },
    {
      value: ds_cm_Campaign_retrieveAllFrontendEnhanced_order.ID,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.id'),
    },
    {
      value: ds_cm_Campaign_retrieveAllFrontendEnhanced_order.LEVEL,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.level'),
    },
    {
      value: ds_cm_Campaign_retrieveAllFrontendEnhanced_order.NAME,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.campaign'),
    },
    {
      value: ds_cm_Campaign_retrieveAllFrontendEnhanced_order.FROM_TIME,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.fromTime'),
    },
    {
      value: ds_cm_Campaign_retrieveAllFrontendEnhanced_order.TILL_TIME,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.tillTime'),
    },
    {
      value: ds_cm_Campaign_retrieveAllFrontendEnhanced_order.MODIFIED_AT,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.modifiedAt'),
    },
    {
      value: ds_cm_Campaign_retrieveAllFrontendEnhanced_order.CREATED_AT,
      direction: Order.asc,
      label: this.translate.instant('lang.globals.createdAt'),
    },
  ] as const;
  orderOption = signal<OrderOption<ds_cm_Campaign_retrieveAllFrontendEnhanced_order>>({ ...this.allOrderOptions[0] });
  showSideBar: boolean;
  form = new FormGroup({
    Title: new FormControl('', [Validators.required]),
  });
  reportName: string;
  reportTill: SbLocalDateTime;
  reportFrom: SbLocalDateTime;
  till: SbLocalDateTime;
  from: SbLocalDateTime;

  constructor(
    protected readonly api: API,
    private readonly userService: UserService,
    public readonly modal: ModalService,
    public readonly modalNavigation: ModalNavigationService,
    public readonly errorHandler: ErrorService,
    public readonly util: UtilService,
    private readonly campaignsService: CampaignReportingObsService,
    public readonly translate: TranslateService,
    public readonly tagService: TagService,
    private readonly queryParamService: QueryParamService,
    private readonly reportingService: ReportingService,
    public readonly permissionService: PermissionService,
    private readonly filterStorage: FilterStorageService,
    private readonly bookingService: BookingService
  ) {
    super();
    this.reportName = null;
    this.resetOrderOption();
    this.campaigns = [];
    this.selectedCompany = null;
    this.filteredCampaignTypes = [];
    this.selectedContentCullIds = [];
    this.timingPlanned = true;
    this.timingActive = true;
    this.timingEnded = false;
    this.timingUnknown = true;
    this.initialBooleans = [];
    this.showSideBar = false;
    this.till = null;
    this.from = null;
  }

  get Title() {
    return this.form.get('Title');
  }

  private registerFormChangesListener() {
    this.Title.valueChanges.subscribe(value => {
      this.reportName = value;
    });
  }

  localDateTimeInputFrom(date: SbLocalDateTime) {
    this.reportFrom = date;
  }

  localDateTimeInputTill(date: SbLocalDateTime) {
    this.reportTill = date;
  }

  resetOrderOption() {
    // reset to default ordering, copy object properties to avoid changing original default
    this.orderOption.set({ ...this.allOrderOptions[0] });
  }

  setToOrderAndRefresh(pValue: ds_cm_Campaign_retrieveAllFrontendEnhanced_order) {
    this.setToOrderOption(pValue);
    this.requestDebounced();
  }

  private setToOrderOption(pValue: ds_cm_Campaign_retrieveAllFrontendEnhanced_order) {
    const foundOrderOption = this.allOrderOptions.find(option => option.value === pValue);
    if (!pValue || !foundOrderOption) {
      // fallback to default if nothing is set or orderOption cannot be found in allOrderOptions
      this.resetOrderOption();
      return;
    }
    const currentOrderOption = this.orderOption();
    if (currentOrderOption.value === pValue) {
      if (currentOrderOption.direction === Order.asc) {
        this.orderOption.set({ ...currentOrderOption, direction: Order.desc });
      } else {
        this.orderOption.set({ ...currentOrderOption, direction: Order.asc });
      }
    } else {
      this.orderOption.set({ ...foundOrderOption });
    }
  }

  getOrder(): { by: ds_cm_Campaign_retrieveAllFrontendEnhanced_order; direction: Order } {
    // if no orderOption -> return default order
    if (!this.orderOption().value) {
      this.resetOrderOption();
      return {
        by: ds_cm_Campaign_retrieveAllFrontendEnhanced_order.CLIENT_ORDER_NUM,
        direction: Order.asc,
      };
    }
    // if value equals column title
    return {
      by: this.orderOption().value,
      direction: this.orderOption().direction,
    };
  }

  ngOnInit() {
    if (this.isInModalContext) {
      this.modalType = this.modalNavigation.getCurrentNavigation().data.modalType;
    }
    this.userService.selectedCompany.subscribe((company: CompanyDTO) => {
      if (company) {
        this.selectedCompany = company;
        this.requestDebounced();
      }
    });
    this.filterStorage.fetch('COMMERCIAL');
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.campaign_show])) {
        item.sbButtons.push({
          icon: 'details',
          tooltip: 'lang.globals.details',
          link: RoutingID.commercials,
          pathParams: [{ id: 'campaignID', value: item?.id }],
          queryParams: {
            timingActive: this.timingActive,
            timingEnded: this.timingEnded,
            timingPlanned: this.timingPlanned,
            timingUnknown: this.timingUnknown,
            contentCulls: this.queryParamService.getQueryParam('contentCulls'),
          },
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.campaign_edit])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.campaignEdit,
          pathParams: [{ id: 'campaignID', value: item?.id }],
        });
        item.sbButtons.push({
          icon: 'copy',
          tooltip: 'lang.globals.copyCampaign',
          action: 'copyCampaign',
        });
      }

      if (this.permissionService.hasPermissions([this.permissionService.Permission.campaign_change_order])) {
        item.sbButtons.push({
          icon: 'rearrange',
          tooltip: 'lang.globals.rearrange',
          action: 'rearrange',
        });
      }

      if (this.permissionService.hasPermissions([this.permissionService.Permission.campaign_delete])) {
        item.sbButtons.push({
          icon: 'trash',
          iconClass: 'hover:!tw-bg-sbRed-500 *:tw-text-sbRed-500',
          action: 'delete',
          tooltip: 'lang.globals.deleteCampaign',
        });
      }
    });
    return result;
  }

  resolveSplitButtonAction(campaign: CampaignWithPreviewReportingDTOAndTagsAndButtons, $event: SplitButtonAction) {
    switch ($event) {
      case 'rearrange':
        this.rearrangeCampaign(campaign);
        break;
      case 'copyCampaign':
        this.copyCampaignModal(campaign);
        break;
      case 'delete':
        this.deleteCampaign(campaign);
        break;
      default:
        console.error('Missing action implementation of Splitbutton');
        break;
    }
  }

  rearrangeCampaign(pCampaign: CampaignWithPreviewDTO) {
    const campaignData: CampaignArrangementModalParams = {
      campaign: pCampaign,
      company: this.selectedCompany?.id ?? null,
      timingPlanned: this.timingPlanned,
      timingActive: this.timingActive,
      timingEnded: this.timingEnded,
      timingUnknown: this.timingUnknown,
    };
    if (pCampaign?.id) {
      this.modal
        .showNavigationModal({
          icon: 'rearrange',
          title: this.translate.instant('lang.globals.rearrange') + ': ' + pCampaign.name,
          entryComponent: { value: SbCampaignOrderingComponent, data: campaignData },
          modalOptions: {
            size: 'lg',
            // windowClass: 'bigXL'
          },
        })
        .then(() => {
          this.requestDebounced();
        })
        .catch(() => {
          this.requestDebounced();
        });
    }
  }

  getTimingSelections(): TimingStatus[] {
    const ret = [];
    if (this.timingPlanned) {
      ret.push(TimingStatus.Future);
    }
    if (this.timingActive) {
      ret.push(TimingStatus.Present);
    }
    if (this.timingEnded) {
      ret.push(TimingStatus.Past);
    }
    if (this.timingUnknown) {
      ret.push(TimingStatus.Unknown);
    }
    return ret.length > 0 ? ret : null;
  }

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const sub = this.api
      .ds_cm_Campaign_retrieveAllFrontendEnhanced(
        {
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
          timingStatus: this.getTimingSelections(),
          from: this.from ?? null,
          till: this.till ?? null,
          company: this.selectedCompany?.id ? [this.selectedCompany.id] : null,
          campaignType: this.filteredCampaignTypes,
          contentCull: this.selectedContentCullIds,
          order: this.getOrder(),
        },
        this.pagination
      )
      .pipe(
        take(1),
        switchMap(campaigns => this.tagService.getAndAddTagEntitiesObs(this.tagService.TaggableDTO.Campaign, campaigns)),
        switchMap(campaigns => this.campaignsService.getAndMergePulseChart(campaigns, 'CampaignWithPreviewDTO')),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.recalculateSelectionState();
          this.loading.set(false);
        })
      )
      .subscribe({
        next: data => {
          this.campaigns = this.buildSplitButtons(data);

          this.cdRef.detectChanges();
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  toggleCampaignEnabled(campaign: CampaignWithPreviewDTO, e) {
    let message: string;
    let title: string;

    if (e === false) {
      title = this.translate.instant('lang.campaigns.disableCampaign');
      message = this.translate.instant('lang.campaigns.disableCampaignQuestion');
    } else {
      title = this.translate.instant('lang.campaigns.enableCampaign');
      message = this.translate.instant('lang.campaigns.enableCampaignQuestion');
    }
    this.modal
      .confirm({
        title,
        message,
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          campaign.active = e;
          this.api
            .ds_cm_Campaign_editCampaign(campaign)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {},
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        } else {
          campaign.active = !e;
        }
      });
  }

  deleteCampaign(campaign: CampaignDTO) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.campaigns.deleteCampaign'),
        message: this.translate.instant('lang.campaigns.deleteCampaignQuestion', { campaign: campaign.name }),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then((result: boolean) => {
        if (result) {
          this.loading.set(true);
          this.api
            .ds_cm_Campaign_deleteById(campaign.id)
            .pipe(takeUntilDestroyed(this.destroyRef), take(1))
            .subscribe({
              next: () => {
                this.requestDebounced();
              },
              error: error => {
                this.loading.set(false);
                this.errorHandler.errorHandler(error);
              },
            });
        }
      });
  }

  // Modal actions
  selectCampaign(selectedCampaign: CampaignWithPreviewReportingDTOAndTags) {
    if (this.modalType === 'reservation') {
      this.modalNavigation.result({ campaign: selectedCampaign });
    } else {
      // this.operatingMode === 'selection'
      const found = this.selectedCampaigns.has(selectedCampaign.id);
      if (found) {
        this.selectedCampaigns.delete(selectedCampaign.id);
      } else {
        this.selectedCampaigns.set(selectedCampaign.id, selectedCampaign);
      }
      this.recalculateSelectionState();
    }
  }

  openSidePanelCreateCampaignExport() {
    this.showSideBar = true;
    this.registerFormChangesListener();
  }

  createCampaignReport() {
    if (!this.reportName || this.selectedCampaigns.size === 0) {
      throw new Error('Reportconfig is not valid');
    }

    this.showSideBar = false;
    this.api
      .ds_cm_Campaign_report({
        campaign: Array.from(this.selectedCampaigns.keys()),
        from: this.reportFrom,
        till: this.reportTill,
        reportName: this.reportName,
      })
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.reportFrom = null;
          this.reportTill = null;
          this.reportName = null;
        })
      )
      .subscribe({
        next: (rep: ReportingFullDTO) => {
          this.reportingService.addReport(rep);
          this.modal
            .showNavigationModal({
              entryComponent: {
                value: ReportingModalComponent,
                data: rep,
              },

              title: this.translate.instant('lang.globals.download'),
              modalOptions: { windowClass: 'M' },
            })
            .then(() => {})
            .catch(() => {});
        },
      });
  }

  handleDateChange(dates: { fromDate: SbDateTime; toDate: SbDateTime }) {
    if (dates.fromDate && dates.toDate) {
      this.from = dates?.fromDate ? new SbLocalDateTime(dates.fromDate?.value) : null;
      this.till = dates?.toDate ? new SbLocalDateTime(dates.toDate?.value) : null;
      this.requestDebounced();
    }
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  copyCampaignModal(campaign: CampaignWithPreviewReportingDTOAndTags) {
    this.modal
      .confirm({
        title: this.translate.instant('lang.globals.copyCampaign'),
        message: this.translate.instant('lang.globals.copyCampaign.message'),
        yes: this.translate.instant('lang.globals.yes'),
        no: this.translate.instant('lang.globals.no'),
      })
      .then(result => {
        if (result) {
          this.copyCampaign(campaign);
        }
      })
      .catch(() => {});
  }

  private copyCampaign(campaign: CampaignWithPreviewReportingDTOAndTags) {
    this.loading.set(true);
    let copiedCampaign: CampaignDTO = { ...campaign, id: null };
    this.api
      .ds_cm_Campaign_createCampaign(campaign.customer.id, copiedCampaign)
      .pipe(
        switchMap(createdCampaign => {
          copiedCampaign = createdCampaign;
          return this.api.ds_cm_Campaign_retrieveCommercialsByCampaignId(
            campaign.id,
            {
              timingStatus: [TimingStatus.Future, TimingStatus.Present],
            },
            new BehaviorSubject<Pagination>({ count: 200, page: 1, pages: -1, resultcount: -1 })
          );
        }),
        switchMap((commercials: CommercialFullDTO[]) => {
          const commercialObs = commercials.map(commercial => this.copyCommercial(commercial, copiedCampaign.id)) ?? [];
          return from(commercialObs);
        }),
        concatMap(obs => obs),
        finalize(() => {
          this.requestDebounced();
        })
      )
      .subscribe({
        next: () => {},
        error: () => {},
      });
  }

  private copyCommercial(commercial: CommercialFullDTO, campaignId: number) {
    let copiedCommercial: CommercialFullDTO;
    copiedCommercial = { ...commercial, requirements: [], id: null };
    let pRequirements: RequirementFullDTO[] = [];
    return forkJoin({
      reqs: this.api.ds_cm_Commercial_retrieveRequirements(commercial.id),
      com: this.api.ds_cm_Campaign_createCommercial(campaignId, copiedCommercial),
    }).pipe(
      switchMap(result => {
        copiedCommercial = result.com;
        pRequirements = result.reqs;
        return from(this.bookingService.createRequirements(copiedCommercial, null, result.reqs));
      }),
      switchMap(createdReq => {
        copiedCommercial.requirements = createdReq;
        return from(this.bookingService.createBookings(createdReq));
      }),
      switchMap((createdBookings: Map<number, BookingDTO[]>) => {
        // check if requirement count from original and copy match
        createdBookings.forEach((bookings, index) => {
          copiedCommercial.requirements[index].bookings = [...bookings];
          // set Media to bookings
          const previewMediaSet: Set<MediumDTO> = new Set();
          pRequirements[index].bookings.forEach(reqBooking => {
            const booking = copiedCommercial.requirements[index].bookings.find(
              lookUpBooking => lookUpBooking.channel?.id === reqBooking.channel?.id
            );
            booking.excluded = reqBooking.excluded;
            if (reqBooking.medium) {
              booking.medium = {
                ...reqBooking.medium,
              };
              previewMediaSet.add({ ...reqBooking.medium });
            }
          });
          // Mutable cast for editable since previewMedia is readonly
          (copiedCommercial.requirements[index] as Mutable<RequirementFullDTO>).previewMedia = Array.from(previewMediaSet.values());
        });
        const editedBookingPromises = copiedCommercial.requirements.map((requirement: RequirementFullDTO) => {
          return from(this.bookingService.editBookings(requirement.bookings));
        });
        return forkJoin(editedBookingPromises).pipe(
          switchMap(editedBookings => {
            editedBookings.forEach((editBookings, index) => {
              copiedCommercial.requirements[index].bookings = [...editBookings];
            });
            return of(commercial);
          })
        );
      }),
      take(1)
    );
  }
  setRequestOptions(filterMap: Map<string, any[]>) {
    this.timingPlanned = filterMap.get('timingPlanned')?.[0];
    this.timingActive = filterMap.get('timingActive')?.[0];
    this.timingEnded = filterMap.get('timingEnded')?.[0];
    this.timingUnknown = filterMap.get('timingUnknown')?.[0];
    this.selectedCompany = filterMap.get('companies')?.[0];
    this.filteredCampaignTypes = filterMap.get('campaignTypes')?.map(type => type.id);
    this.selectedContentCullIds = filterMap.get('contentCulls')?.map(cull => cull.id);
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    const orderOption = filterMap.get('orderOption')?.[0];
    const orderDirection = filterMap.get('orderDirection')?.[0];
    this.setOrderOptionFromPanel(orderOption, orderDirection);
  }

  private setOrderOptionFromPanel(value: ds_cm_Campaign_retrieveAllFrontendEnhanced_order, pDirection: Order) {
    const foundOrderOption = this.allOrderOptions.find(option => option.value === value);
    if (!foundOrderOption) {
      // fallback to default if nothing is set or orderOption cannot be found in allOrderOptions
      this.resetOrderOption();
      return;
    }
    this.orderOption.set({ ...foundOrderOption, direction: pDirection });
  }

  checkExpansion() {
    return this.filterPanelRef?.isPanelExtended() ?? false;
  }

  openFilterPanel(elementId: HTMLDivElement) {
    if (!elementId) {
      throw new Error('Missing elementId for filter panel opening');
    }
    if (this.filterPanelRef) {
      this.filterPanelRef.isPanelExtended.set(true);
      this.scrollToFilterPanelElement(elementId, false);
    }
  }

  scrollToFilterPanelElement(element: HTMLDivElement, isInitiallyExpanded: boolean = true) {
    if (!element) {
      throw new Error('Missing elementId for filter panel opening');
    }
    const timeout = setTimeout(
      () => {
        const scrollContainer = element.closest('.custom-scrollbar') as HTMLElement;

        if (!scrollContainer) {
          console.warn('No scrollable container found for element');
          return;
        }
        const containerTop = scrollContainer.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;

        const headerOffset = 110; // height filter header + height of navbar + offset
        const offsetPosition = elementTop - containerTop + scrollContainer.scrollTop - headerOffset;

        scrollContainer.scrollTo({ behavior: 'smooth', top: offsetPosition });
        // elementId.scrollBy({ top: 40 });
        clearTimeout(timeout);
      },
      isInitiallyExpanded ? 100 : 400
    );
  }

  private recalculateSelectionState() {
    if (!this.campaigns || this.campaigns.length === 0) {
      this.selectionState = 'none';
      return;
    }
    const allSelectedOnCurrentPage = this.campaigns.every(campaign => this.selectedCampaigns.has(campaign.id));
    if (allSelectedOnCurrentPage) {
      this.selectionState = 'all';
      return;
    }
    const someSelectedOnCurrentPage = this.campaigns.some(campaign => this.selectedCampaigns.has(campaign.id));
    if (someSelectedOnCurrentPage) {
      this.selectionState = 'partly';
      return;
    }
    this.selectionState = 'none';
  }

  selectAction($event: MultiSelectButtonAction) {
    if (!this.campaigns || this.campaigns.length === 0) {
      return;
    }
    switch ($event) {
      case 'selectPage':
        this.campaigns.forEach(campaign => {
          this.selectedCampaigns.set(campaign.id, campaign);
        });
        this.selectionState = 'all';
        break;
      case 'deselectAll':
        this.selectedCampaigns.clear();
        this.selectionState = 'none';
        break;
      case 'deselectPage':
      default:
        this.campaigns.forEach(campaign => {
          this.selectedCampaigns.delete(campaign.id);
        });
        this.selectionState = 'none';
        break;
    }
  }

  identity(data: any): CampaignWithPreviewReportingDTOAndTagsAndButtons {
    return data;
  }
}
